/**
 * ConfigPanel - Sliders and inputs to adjust proctoring thresholds
 */

import { useProctoringConfigStore } from '../../store/proctoringConfigStore'
import type { AssessmentProctoringConfig } from '@really-assess/shared/assessments'
import { ProctoringConfigFields } from '../proctoring/ProctoringConfigFields'
import { ProctoringTextInput } from '../proctoring/proctoring-config-inputs'

export default function ConfigPanel() {
  const config = useProctoringConfigStore()

  const assessmentConfig: AssessmentProctoringConfig = {
    verificationIntervalMs: config.verificationIntervalMs,
    noFaceWarningThresholdMs: config.noFaceWarningThresholdMs,
    identitySimilarityThreshold: config.identitySimilarityThreshold,
    maxConsecutiveIdentityFailures: config.maxConsecutiveIdentityFailures,
    scoringWeights: { ...config.scoringWeights },
    violationPenalties: { ...config.violationPenalties },
    passingScore: config.passingScore,
    completionPollIntervalMs: config.completionPollIntervalMs,
    timeLimit: { ...config.timeLimit },
    browserLockdown: config.browserLockdown,
  }

  const handleExport = () => {
    const exportData = config.exportConfig()
    const code = `// Proctoring Configuration
// Generated: ${new Date().toISOString()}

export const PROCTORING_CONFIG = ${JSON.stringify(exportData, null, 2)}`

    navigator.clipboard.writeText(code)
    alert('Configuration copied to clipboard!')
  }

  return (
    <div className="bg-gray-800 rounded-xl p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Configuration</h2>
        <button
          onClick={handleExport}
          className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-500 rounded transition-colors"
        >
          Export
        </button>
      </div>

      <section>
        <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">ReallyAvatar</h3>
        <p className="text-xs text-gray-500 mb-3">
          For per-assessment URLs and webhooks, use the admin panel. These global values apply on /proctoring-test
          and as fallbacks when an assessment field is not set.
        </p>
        <div className="space-y-4">
          <ProctoringTextInput
            label="Assessment URL"
            description="ReallyAvatar page URL (iframe source)"
            value={config.externalAssessmentUrl}
            onChange={config.setExternalAssessmentUrl}
            placeholder="https://reallyavatar.example.com/assessment"
          />
          <ProctoringTextInput
            label="Integrity Webhook URL"
            description="ReallyAvatar endpoint to receive the final integrity score"
            value={config.completionWebhookUrl}
            onChange={config.setCompletionWebhookUrl}
            placeholder="https://reallyavatar.example.com/api/integrations/reallyassess/webhook"
          />
          <ProctoringTextInput
            label="Webhook Secret"
            description="HMAC secret for signing webhook payloads (optional)"
            value={config.completionWebhookSecret}
            onChange={config.setCompletionWebhookSecret}
            placeholder="whsec_..."
            type="password"
          />
        </div>
      </section>

      <ProctoringConfigFields
        variant="test"
        showScoringHelp
        value={assessmentConfig}
        onChange={config.setAssessmentProctoringConfig}
      />
    </div>
  )
}
